import { Link } from 'react-router-dom';

interface Reason {
  icon: string;
  color: string;
  title: string;
  description: string;
}

interface SharedWhyChooseUsProps {
  title?: string;
  subtitle?: string;
  reasons: Reason[];
  showComparison?: boolean;
  bgClass?: string;
}

const comparisonRows = [
  { feature: 'Cure Time', phix: '1–3 min sunlight', traditional: '4–12 hours', solarez: '3–5 min UV lamp' },
  { feature: 'Shelf Life', phix: 'Endless (select)', traditional: '6–12 months', solarez: '12 months' },
  { feature: 'Board Types', phix: 'All boards', traditional: 'All boards', solarez: 'Epoxy/Poly only' },
  { feature: 'Travel Safe', phix: 'TSA approved', traditional: 'No', solarez: 'Restricted' },
  { feature: 'Bio-Resin Option', phix: 'Yes', traditional: 'No', solarez: 'No' },
  { feature: 'Hardens in tube', phix: 'No', traditional: 'No', solarez: 'Yes (common)' },
];

export default function SharedWhyChooseUs({
  title = 'Why Surfers Choose PHIX Doctor',
  subtitle,
  reasons,
  showComparison = true,
  bgClass = 'bg-[#F5EFE6]',
}: SharedWhyChooseUsProps) {
  return (
    <section className={`py-24 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#FF6B35] text-sm font-bold uppercase tracking-widest mb-3">The PHIX Difference</p>
          <h2 className="text-4xl md:text-5xl font-black text-stone-900">
            Why Surfers Choose<br />
            <span className="text-[#0B5C4D]">PHIX Doctor</span>
          </h2>
          {subtitle && <p className="text-stone-500 text-lg mt-4 max-w-xl mx-auto">{subtitle}</p>}
        </div>

        {/* Reasons grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {reasons.map((reason) => (
            <div
              key={reason.title}
              className="bg-white rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300 cursor-default"
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${reason.color}`}>
                <i className={`${reason.icon} text-2xl`} />
              </div>
              <h3 className="text-stone-900 font-bold text-lg mb-3">{reason.title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        {showComparison && (
          <div>
            <h3 className="text-2xl font-black text-stone-900 text-center mb-8">
              PHIX Doctor vs. <span className="text-[#0B5C4D]">The Alternatives</span>
            </h3>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid grid-cols-4 bg-[#0B5C4D] text-white text-sm font-bold">
                <div className="p-4">Feature</div>
                <div className="p-4 text-[#FF6B35]">PHIX Doctor</div>
                <div className="p-4 opacity-70">Traditional Resin</div>
                <div className="p-4 opacity-70">Solarez</div>
              </div>
              {comparisonRows.map((row, idx) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-4 text-sm border-b border-stone-100 last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
                >
                  <div className="p-4 font-semibold text-stone-700">{row.feature}</div>
                  <div className="p-4 font-bold text-[#0B5C4D] flex items-center gap-1.5">
                    <i className="ri-check-line text-[#0B5C4D]" />
                    {row.phix}
                  </div>
                  <div className="p-4 text-stone-400">{row.traditional}</div>
                  <div className="p-4 text-stone-400">{row.solarez}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-stone-400 text-xs mt-4">Comparison based on standard market products. Results may vary.</p>
          </div>
        )}

        {/* Mid CTA */}
        <div className="text-center mt-12">
          <Link
            to="/products"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-8 py-4 rounded-full hover:bg-[#e55a25] transition-colors cursor-pointer"
          >
            Shop Repair Kits Now
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
